import clsx from 'clsx';
import React, { Suspense, lazy, useState } from 'react';

import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { ContentType, Page } from '@tracking';

import Data from './Layouts/Data';
import Feature from './Layouts/Feature';
import Graphic from './Layouts/Graphic';
import LockedArticle from './Layouts/LockedArticle';
import Standard from './Layouts/Standard';
import Weekend from './Layouts/Weekend';
import { type ArticleProps, ArticleTheme, type ThemeViewsProps } from './interfaces';

const Paywall = lazy(() => import('./Paywall'));

const UNLOCKED_VIA_EMAIL = 'entered_email';
const UNLOCKED_VIA_FREE_FOR_KNOWN_LEADS = 'free_for_known_leads';

const getArticleTemplate = (theme: string) => {
  switch (theme) {
    case ArticleTheme.Feature:
      return Feature;
    case ArticleTheme.Graphic:
      return Graphic;
    case ArticleTheme.Weekend:
      return Weekend;
    case ArticleTheme.Data:
      return Data;
    default:
      return Standard;
  }
};

const ArticleLayout = ({
  article,
  canShareArticle,
  canViewFullArticle,
  canViewComments,
  clipperProps,
  endOfArticleDeepResearchPrompts,
  env,
  hideAwarenessBar,
  isSEOBotAndArticleIsNotFree,
  initialCurrentUser,
  isWebview,
  midArticleUnitType,
  mostPopularArticles,
  newsletterId,
  offerCode,
  relatedArticles,
  relatedEvent,
  shareGuid,
  suggestedAiPrompts,
  theme,
  unlockedVia,
}: ArticleProps) => {
  const [audioActive, setAudioActive] = useState(false);
  const themeProps: ThemeViewsProps = {
    article,
    audioActive,
    canShareArticle,
    canViewComments,
    canViewFullArticle,
    clipperProps,
    endOfArticleDeepResearchPrompts,
    env,
    initialCurrentUser,
    isSEOBotAndArticleIsNotFree,
    isWebview,
    midArticleUnitType,
    mostPopularArticles,
    newsletterId,
    relatedArticles,
    relatedEvent,
    setAudioActive,
    suggestedAiPrompts,
  };
  const ArticleTemplate = getArticleTemplate(theme);
  const hydrated = useServerSideRendering();
  const { Track } = useEventTracking({
    contentId: article.id,
    contentType: ContentType.Article,
    page: Page.Article,
    pageId: article.id,
  });
  const shouldSkipFullBleedPaywall =
    (article.isPro &&
      initialCurrentUser?.is_subscriber &&
      !initialCurrentUser?.is_pro_subscriber) ||
    canViewFullArticle;
  const showPaywall =
    !canViewFullArticle ||
    [UNLOCKED_VIA_FREE_FOR_KNOWN_LEADS, UNLOCKED_VIA_EMAIL].includes(unlockedVia);

  return (
    <Track>
      <article
        className={clsx('relative', {
          'pt-32 sm:pt-48': !isWebview && !showPaywall,
          'pt-90 sm:pt-[140px]':
            !isWebview && showPaywall && (shouldSkipFullBleedPaywall || hideAwarenessBar),
          'pt-[155px] sm:pt-[140px]':
            !isWebview && !shouldSkipFullBleedPaywall && showPaywall && !hideAwarenessBar,
        })}
      >
        {!shouldSkipFullBleedPaywall &&
        showPaywall &&
        !isSEOBotAndArticleIsNotFree &&
        !isWebview ? (
          <LockedArticle offerCode={offerCode} shareGuid={shareGuid} {...themeProps} />
        ) : (
          <ArticleTemplate {...themeProps} />
        )}
      </article>
      {/* Display this paywall only when:
          - The article is free and targeted at known leads, or
          - The user is a subscriber with an annual plan but requires a Pro subscription */}
      {hydrated && showPaywall && shouldSkipFullBleedPaywall && (
        <Suspense>
          <div
            className={clsx('sticky -bottom-px z-50 mt-0 w-full bg-grayscale-charcoal')}
            data-testid="paywall-container"
          >
            <Paywall
              articleId={article.id}
              isTwoOptPaywall
              offerCode={offerCode}
              shareGuid={shareGuid}
            />
          </div>
        </Suspense>
      )}
    </Track>
  );
};

export default ArticleLayout;
